import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import PublicNavbar from '../components/PublicNavbar';
import Navbar from '../components/Navbar';
import LoginModal from '../components/LoginModal';
import './AboutUs.css';

function AboutUs() {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      {user ? (
        <Navbar />
      ) : (
        <PublicNavbar onSignInClick={() => setIsModalOpen(true)} />
      )}

      <div className="about-page">

        {/* ── HERO ── */}
        <div className="about-hero">
          <div className="about-hero-content">
            <div className="lp-eyebrow">Our Story</div>
            <h1 className="about-hero-title">
              Built for the Parent in the Stands
            </h1>
            <p className="about-hero-subtitle">
              Not the coach in the dugout.
            </p>
          </div>
        </div>

        {/* ── STORY ── */}
        <div className="about-section about-section-dark">
          <div className="about-section-content about-split">
            <div className="about-split-text">
              <div className="lp-eyebrow">How It Started</div>
              <h2 className="about-heading">The Real Origin Story</h2>
              <p className="about-body">
                My son Reid is a left-handed pitcher and first baseman for his
                high school team. Every game, I'd sit in the stands trying to
                track his outings: pitch counts, strikeouts, earned runs, how
                he was working the corners at first. Scribbling notes on my
                phone, trying to remember what happened in the third inning by
                the time we got home.
              </p>
              <p className="about-body">
                I looked for a tool that could help. Everything I found was
                designed for coaches tracking full rosters. Nothing was built
                for a parent who just wants to know how their kid is really
                developing.
              </p>
              <p className="about-body">
                So I built it myself.
              </p>
              <p className="about-body">
                Playmaker Media Kit started as a graphics tool for coaches.
                Then Reid had a great outing and I wanted to share it. The
                real story wasn't the graphic. It was the data behind it.
                That's when everything shifted.
              </p>
              <p className="about-body">
                Today, Playmaker is a player development platform for baseball
                and softball parents. Track your player live. Understand what
                the numbers mean. Dig deeper when you want answers. And brag
                a little when they earn it.
              </p>
            </div>
            <div className="about-split-visual">
              <img
                src="/PMLogo.png"
                alt="Playmaker Media Kit"
                className="about-logo-img"
              />
            </div>
          </div>
        </div>

        {/* ── WHAT WE DO ── */}
        <div className="about-section about-section-navy">
          <div className="about-section-content about-centered">
            <div className="lp-eyebrow">What We Do</div>
            <h2 className="about-heading">The Journey is Simple.</h2>
            <p className="about-body about-body-centered">
              Playmaker is a player development platform built specifically
              for baseball and softball parents. Everything we build serves
              one purpose: helping you understand and celebrate your player's
              journey.
            </p>
            <div className="about-steps-grid">
              <div className="about-step-card">
                <div className="about-step-number">1</div>
                <h3>Track</h3>
                <p>
                  Follow every at-bat, pitch, and play for your player.
                  Only your player. No roster management. No full team
                  lineups. Just the stats that matter to you.
                </p>
              </div>
              <div className="about-step-card">
                <div className="about-step-number">2</div>
                <h3>Understand</h3>
                <p>
                  Development Insights surface trends from your player's real game data. Know exactly where they're excelling and where they need work.
                </p>
              </div>
              <div className="about-step-card">
                <div className="about-step-number">3</div>
                <h3>Dig Deeper</h3>
                <p>
                  Ask the Analyst anything about your player's game and get personalized answers based on stats you actually tracked.
                </p>
              </div>
              <div className="about-step-card about-step-card-bonus">
                <div className="about-step-bonus-icon">⭐</div>
                <h3>Brag a Little</h3>
                <p>
                  Turn great games into BragCards worth sharing. Professional graphics built from your player's real stats. Ready for Instagram, Facebook, and more.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ── DIFFERENTIATOR ── */}
        <div className="about-section about-section-crimson">
          <div className="about-section-content about-centered">
            <div className="lp-eyebrow">Why Playmaker</div>
            <h2 className="about-heading">What Makes Us Different</h2>
            <div className="about-differences-grid">
              <div className="about-difference-item">
                <h3>You Control the Data</h3>
                <p>
                  No waiting on a coach to enter stats. No missing games.
                  No padded numbers. Just the truth about your player's
                  development. Because you tracked it yourself.
                </p>
              </div>
              <div className="about-difference-item">
                <h3>Built for One Player</h3>
                <p>
                  Not a roster tool. Not a team management app. Everything
                  is focused on your player and their story. This is just
                  for you and your kid.
                </p>
              </div>
              <div className="about-difference-item">
                <h3>Ask the Analyst</h3>
                <p>
                  Ask anything about your player's game and get personalized answers based on stats you actually tracked. No generic advice. Just answers built around your player's real data.
                </p>
              </div>
              <div className="about-difference-item">
                <h3>Graphics Included</h3>
                <p>
                  Free baseball and softball graphics for coaches and orgs.
                  Game previews, recaps, tryout announcements, and player
                  spotlights for your whole team. No design skills needed.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ── WHO WE SERVE ── */}
        <div className="about-section about-section-dark">
          <div className="about-section-content about-centered">
            <div className="lp-eyebrow">Who We Serve</div>
            <h2 className="about-heading">Built for Everyone in the Ballpark</h2>
            <div className="about-audience-grid">
              <div className="about-audience-card">
                <h3>Parents</h3>
                <p>
                  Track your player's development, understand where they're
                  excelling and where they need work, and share their biggest
                  moments with pride.
                </p>
              </div>
              <div className="about-audience-card">
                <h3>Coaches</h3>
                <p>
                  Free baseball and softball graphics for game previews,
                  recaps, tryout announcements, and player spotlights for
                  your whole team.
                </p>
              </div>
              <div className="about-audience-card">
                <h3>Club Administrators</h3>
                <p>
                  Maintain consistent branding across multiple teams with
                  Organization tier access and coach management tools.
                </p>
              </div>
              <div className="about-audience-card">
                <h3>League Directors</h3>
                <p>
                  Consistent, professional graphics across your entire organization. Game previews, recaps, tryout announcements, and team management tools built for leagues.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ── MISSION ── */}
        <div className="about-section about-section-navy">
          <div className="about-section-content about-centered">
            <div className="lp-eyebrow">Our Mission</div>
            <h2 className="about-heading">More Than Wins and Losses</h2>
            <div className="about-mission-box">
              <p className="about-mission-text">
                Youth sports are about development, confidence, and the moments
                you'll talk about for years. Every parent deserves to know how
                their player is really doing. Not just the box score, but the
                full picture.
              </p>
              <p className="about-mission-statement">
                Your player's story deserves to be documented.
              </p>
            </div>
          </div>
        </div>

        {/* ── LOOKING AHEAD ── */}
        <div className="about-section about-section-dark">
          <div className="about-section-content about-centered">
            <div className="lp-eyebrow">Roadmap</div>
            <h2 className="about-heading">Looking Ahead</h2>
            <p className="about-body about-body-centered">
              We're just getting started. Here's what's coming next.
            </p>
            <div className="about-roadmap-list">
              <div className="about-roadmap-item">Animated BragCard GIFs for Pro tier</div>
              <div className="about-roadmap-item">3rd BragCard template design</div>
              <div className="about-roadmap-item">Advanced season analytics and multi-year tracking</div>
              <div className="about-roadmap-item">Video highlight reels with stats overlay</div>
            </div>
          </div>
        </div>

        {/* ── CTA ── */}
        <div className="about-cta-section">
          <div className="about-section-content about-centered">
            <h2 className="about-heading">Ready to Start Tracking?</h2>
            <p className="about-body about-body-centered">
              Free to start. No credit card required. Track your first game tonight.
            </p>
            <div className="about-cta-buttons">
              <button
                className="btn btn-primary about-cta-btn"
                onClick={() => navigate('/register')}
                style={{ background: '#c0001a', border: '2px solid rgba(255,255,255,0.3)', width: 'auto', display: 'inline-block' }}
              >
                Create Free Account
              </button>
              <button
                className="about-cta-btn-secondary"
                onClick={() => navigate('/contact')}
              >
                Contact Us
              </button>
            </div>
          </div>
        </div>

        {/* ── FOOTER TAGLINE ── */}
        <div className="about-footer-tagline">
          <h2 className="about-brand-name">Playmaker Media Kit</h2>
          <p className="about-tagline">Your player's story deserves to be documented.</p>
        </div>

      </div>

      <Footer />
      <LoginModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
}

export default AboutUs;
